import type { PathStep, ReadyModule } from '~/pages/backoffice/admin/sessions/path-step'
import { CertificateCondition } from '@zeus/index'
import { defaultMinScore } from '~/pages/backoffice/admin/sessions/session-path-form'

/** Libellé de chaque condition d'attestation, partagé par le formulaire et la fiche session. */
export const CERTIFICATE_CONDITION_KEYS: Record<CertificateCondition, string> = {
  [CertificateCondition.COMPLETION]: 'sessions.certificate.COMPLETION',
  [CertificateCondition.SUCCESS]: 'sessions.certificate.SUCCESS',
  [CertificateCondition.SCORE]: 'sessions.certificate.SCORE',
}

/** La condition d'attestation telle que le formulaire la manipule (#10). Seuil sur [0..1]. */
export type CertificateRule = { condition: CertificateCondition, minScore: number | null }

/** La condition enregistrée, ou la complétude — le comportement d'avant — pour un nouveau parcours. */
export function certificateRuleFrom(session?: {
  certificateCondition?: string | null
  certificateMinScore?: number | null
}): CertificateRule {
  return {
    condition: (session?.certificateCondition as CertificateCondition | undefined) ?? CertificateCondition.COMPLETION,
    minScore: session?.certificateMinScore ?? null,
  }
}

/**
 * Seuil proposé quand on passe au score : le seuil de réussite déclaré par le
 * premier module du parcours qui en a un — à défaut, le seuil par défaut.
 */
export function suggestedCertificateScore(steps: PathStep[], modules: ReadyModule[]): number {
  const byId = new Map(modules.map(m => [m.id, m]))
  const declared = steps.map(step => byId.get(step.moduleId)?.masteryScore).find(score => score != null)
  return defaultMinScore(declared)
}

/** Change de condition ; passer au score reprend le seuil saisi, sinon le seuil proposé. */
export function withCondition(rule: CertificateRule, condition: CertificateCondition, suggested: number): CertificateRule {
  return {
    condition,
    minScore: condition === CertificateCondition.SCORE ? (rule.minScore ?? suggested) : rule.minScore,
  }
}

/** Ce que l'API reçoit : le seuil n'accompagne que le score. */
export function toCertificateInput(rule: CertificateRule) {
  return {
    certificateCondition: rule.condition,
    certificateMinScore: rule.condition === CertificateCondition.SCORE ? rule.minScore : null,
  }
}

/** La condition a-t-elle changé par rapport à celle enregistrée ? */
export function certificateRuleChanged(before: CertificateRule, after: CertificateRule): boolean {
  const was = toCertificateInput(before)
  const now = toCertificateInput(after)
  return was.certificateCondition !== now.certificateCondition || was.certificateMinScore !== now.certificateMinScore
}

type WarningInput = {
  isEdit: boolean
  enrolledCount: number
  stored: { rule: CertificateRule, steps: PathStep[] }
  current: { rule: CertificateRule, steps: PathStep[] }
}

/**
 * Ce que le formulaire doit dire avant d'enregistrer (#10) :
 *
 * - `mayRevoke` — l'attestation suit la règle EN VIGUEUR, rien n'est figé
 *   (décision produit) : changer la condition, ou le score retenu d'un module
 *   quand elle porte sur la réussite ou le score, peut la retirer à un inscrit ;
 * - `noRetake` — un module qui ne peut pas être refait, sous une condition de
 *   réussite ou de score : qui le rate n'aura jamais l'attestation.
 */
export function certificateWarnings({ isEdit, enrolledCount, stored, current }: WarningInput) {
  const strict = current.rule.condition !== CertificateCondition.COMPLETION
  const retentionBefore = new Map(stored.steps.map(step => [step.moduleId, step.scoreRetention]))
  const retentionChanged = current.steps.some(step =>
    retentionBefore.has(step.moduleId) && retentionBefore.get(step.moduleId) !== step.scoreRetention)

  return {
    mayRevoke: isEdit && enrolledCount > 0
      && (certificateRuleChanged(stored.rule, current.rule) || (strict && retentionChanged)),
    noRetake: strict && current.steps.some(step => !step.allowRetake),
  }
}
